import React from 'react'
import AuditAdminMenu from '../AuditAdminMenu'
import {TemplatesContext} from '../../context/Templates'
import {AuditPaneContext} from '../../context/AuditPane'
import {UserContext} from '../../context/User'
import {useTranslation} from 'react-i18next'
import upperCase from 'lodash/upperCase'
import {Form, InputGroup} from 'react-bootstrap'
import Spinner from 'react-bootstrap/Spinner'
import search_icon from '../../assets/images/search.png'
import moment from 'moment'
import {getLastAuditPeriod, getNextAuditPeriod} from '../../utils/auditDatePeriodFormatter'
import {Table, Column, InfiniteLoader} from 'react-virtualized'

function AuditTemplateTab({tabWidth}) {
  const {
    template,
    templates,
    selectTemplate,
    getAllTemplates,
    setTemplateParams,
    templateParams,
    getNextTemplates,
  } = React.useContext(TemplatesContext)

  const [start, setStart] = React.useState(1)
  const user = React.useContext(UserContext)
  const {isExpandOpen, openSidebar, isSidebarOpen, openModal} = React.useContext(AuditPaneContext)
  const [loading, setLoading] = React.useState(true)
  const searchInput = React.createRef()
  const {t} = useTranslation()
  const permission = (user.details && user.details.canCreate) || false
  const width = tabWidth

  React.useEffect(() => {
    getAllTemplates({start: 1, ...templateParams})
  }, [getAllTemplates, templateParams])

  const handleRecordSelect = (index, data) => {
    if (template === null || (template && template.selected && template.selected.id) !== data.id) {
      selectTemplate(data)
    }
    !isExpandOpen && openSidebar('auditTemplate')
  }

  const handleSearch = event => {
    setTemplateParams({...templateParams, query: event.target.value})
  }

  const handleSearchWithClick = () => {
    const query = (searchInput && searchInput.current && searchInput.current.value) || ''
    setTemplateParams({...templateParams, query})
  }

  const _noRowsRenderer = () => {
    if (
      templates &&
      !templates.isLoading &&
      templates.total_audits === 0 &&
      templateParams.query === ''
    ) {
      return (
        <div className="text_center audit-table__cell--none audit-table__cell--gray audit-table__cell--expand">
          <span className="font_20">
            {t('audit.tab.none.created')}
            <br />
            {permission && t('audit.tab.none.suggestion')}
          </span>
          <br />
          {permission && (
            <button className="audit-button audit-button--blue mt_20" onClick={() => openModal()}>
              {t('audit.button.create')}
            </button>
          )}
        </div>
      )
    } else if (templates && templates.isLoading) {
      return (
        <div className="text_center">
          <Spinner className="audit-spinner mt-15" animation="border" role="status" />
        </div>
      )
    } else {
      return (
        <div className="text_center audit-table__cell--none audit-table__cell--gray audit-table__cell--expand">
          <span className="font_20"> {t('audit.tab.none.found')}</span>
        </div>
      )
    }
  }

  const sortHeader = (data, className = '') => {
    return (
      <div
        className={`audit-table__header audit-icon--sort p-0 ${
          templateParams.column_name === data.dataKey
            ? data.sortDirection === 'ASC'
              ? ' audit-icon--sort-asc'
              : ' audit-icon--sort-desc'
            : ''
        }`}
      >
        <span className={className} title={data.label}>
          {data.label}
        </span>
      </div>
    )
  }

  const loadMore = () => {
    if (loading) {
      setLoading(false)
      getNextTemplates({start: start + 1, ...templateParams}).then(() => {
        setStart(start + 1)
        setLoading(true)
      })
    }
  }

  const _sort = e => {
    let column_name = e.sortBy
    let sort_by = e.sortDirection
    setTemplateParams({...templateParams, column_name, sort_by})
    setStart(1)
  }

  const _rowClassName = index => {
    if (
      index !== -1 &&
      templates.audits &&
      template.selected &&
      templates.audits[index].id === template.selected.id &&
      (isExpandOpen || isSidebarOpen)
    ) {
      return 'audit-table__row audit-table__row--no-border-top cursor_pointer audit-table__row--highlighted'
    } else if (index !== -1 && templates.audits[index].id === 'loading') {
      return 'audit-table__row audit-table__row--no-border-top cursor_pointer loading_row'
    } else {
      return 'audit-table__row audit-table__row--no-border-top cursor_pointer'
    }
  }

  const bubble = content => <span className="ap-bubble ml_5">{content}</span>

  const columnAssignee = data => {
    return (
      <div onClick={() => handleRecordSelect(data.rowIndex, data.rowData)}>
        {data.rowData.audit_users_attributes && data.rowData.audit_users_attributes.length > 1 ? (
          <>
            {`${data.rowData.audit_users_attributes[0].user_name}, `}
            {bubble(`+${data.rowData.audit_users_attributes.length - 1}`)}
          </>
        ) : data.rowData.audit_users_attributes &&
          data.rowData.audit_users_attributes.length !== 0 ? (
          data.rowData.audit_users_attributes[0].user_name
        ) : (
          ''
        )}
      </div>
    )
  }

  const columnNameData = data => {
    if (data.rowData.name === '') {
      return <Spinner className="audit-spinner mt-15" animation="border" role="status" />
    }
    return (
      <div onClick={() => handleRecordSelect(data.rowIndex, data.rowData)}>
        <div className="overflow-content">{data.rowData.name}</div>
      </div>
    )
  }

  const columnEntityData = data => {
    if (data.rowData.name === '') {
      return ''
    }
    return (
      <div onClick={() => handleRecordSelect(data.rowIndex, data.rowData)}>
        {data.rowData.audit_entities_attributes[0].entity_name}
      </div>
    )
  }

  const columnData = (data, value) => {
    return <div onClick={() => handleRecordSelect(data.rowIndex, data.rowData)}>{value}</div>
  }

  const columnAction = data => {
    if (data.rowData.audit_type_for_user === 'created' || user.details.is_admin) {
      return <AuditAdminMenu data={data.rowData} />
    } else {
      return <span className="actionSize"></span>
    }
  }

  return (
    <>
      {!isExpandOpen && (
        <span className={`audit-search ${permission ? '' : ' mt_0'}`}>
          <InputGroup className="audit__input-group">
            <Form.Control
              className="audit__input audit__input--search"
              placeholder={t('audit.tab.search')}
              onKeyPress={event => {
                if (event.key === 'Enter') {
                  handleSearch(event)
                }
              }}
              ref={searchInput}
              defaultValue={templateParams.query}
            />
            <InputGroup.Append>
              <img
                src={search_icon}
                className="audit-search__image"
                alt="img"
                onClick={handleSearchWithClick}
              ></img>
            </InputGroup.Append>
          </InputGroup>
        </span>
      )}
      <div
        className={
          !isExpandOpen
            ? 'audit-tab__table position-unset'
            : 'audit-tab__table position-unset mt-64 expand_page'
        }
      >
        {templates && templates.audits && (
          <>
            <div className="main-container">
              <h1 className="tableTitleText">
                Infinite scrolling autosize table for template table with sorting in each column,
                search and action dropdown for both admin and user
              </h1>
              <InfiniteLoader
                isRowLoaded={({index}) => !!templates.audits[index]}
                loadMoreRows={loadMore}
                rowCount={templates.total_audits}
                threshold={5}
              >
                {({onRowsRendered, registerChild}) => (
                  <Table
                    ref={registerChild}
                    onRowsRendered={onRowsRendered}
                    className="audit-table"
                    headerHeight={33}
                    rowClassName={h => _rowClassName(h.index)}
                    width={width}
                    height={735}
                    rowHeight={40}
                    rowCount={templates.audits.length}
                    rowGetter={({index}) => templates.audits[index]}
                    rowStyle={{overflow: 'visible'}}
                    sort={e => _sort(e)}
                    sortBy={templateParams.column_name}
                    sortDirection={templateParams.sort_by}
                    noRowsRenderer={_noRowsRenderer}
                  >
                    <Column
                      className="audit-table__cell audit-table__cell--pad-left audit-table__cell--expand audit-name__cell"
                      headerRenderer={h =>
                        sortHeader(h, 'ReactVirtualized__Table__headerTruncatedText')
                      }
                      cellRenderer={c => columnNameData(c)}
                      label={upperCase(t('audit.table.columns.template_name'))}
                      dataKey="name"
                      width={width * 0.22}
                      flexGrow={1}
                    />
                    {!isExpandOpen && (
                      <Column
                        headerRenderer={h => sortHeader(h)}
                        className="audit-table__cell"
                        cellRenderer={c => columnEntityData(c)}
                        label={upperCase(t('audit.table.columns.entity_name'))}
                        dataKey="id"
                        width={width * 0.1}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        disableSort={true}
                        cellRenderer={columnAssignee}
                        className="audit-table__cell"
                        label={upperCase(t('audit.table.columns.assignee'))}
                        width={width * 0.12}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        disableSort={true}
                        className="audit-table__cell"
                        cellRenderer={c => columnData(c, c.rowData.used_count)}
                        label={upperCase(t('audit.table.columns.used_count'))}
                        dataKey="used_count"
                        width={width * 0.1}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        headerRenderer={h => sortHeader(h)}
                        className="audit-table__cell"
                        cellRenderer={c =>
                          columnData(c, getLastAuditPeriod(c.rowData.last_audit_period))
                        }
                        label={upperCase(t('audit.table.columns.last_audit_period'))}
                        dataKey="last_audit_period"
                        width={width * 0.12}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        headerRenderer={h => sortHeader(h)}
                        className="audit-table__cell"
                        cellRenderer={c =>
                          columnData(c, getNextAuditPeriod(c.rowData.next_audit_period))
                        }
                        label={upperCase(t('audit.table.columns.next_audit_period'))}
                        dataKey="next_audit_period"
                        width={width * 0.12}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        headerRenderer={h => sortHeader(h)}
                        className="audit-table__cell"
                        cellRenderer={c =>
                          columnData(c, moment(c.rowData.created_at).format('M/DD/YYYY'))
                        }
                        label={upperCase(t('audit.table.columns.created_at'))}
                        dataKey="created_at"
                        width={width * 0.1}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        headerRenderer={h => sortHeader(h)}
                        className="audit-table__cell"
                        label={upperCase(t('audit.table.columns.user_name'))}
                        dataKey="user_name"
                        width={width * 0.12}
                        flexGrow={1}
                      />
                    )}
                    {!isExpandOpen && (
                      <Column
                        disableSort={true}
                        cellRenderer={columnAction}
                        headerClassName={'actionSize'}
                        style={{overflow: 'visible'}}
                        width={width * 0.02}
                        flexGrow={1}
                      />
                    )}
                  </Table>
                )}
              </InfiniteLoader>
            </div>
          </>
        )}
      </div>
    </>
  )
}
export default AuditTemplateTab
